import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useNavigate, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { contractorAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1a1a2e',
      fontFamily: '"Roboto", sans-serif',
      '::placeholder': { color: '#aaa' },
    },
    invalid: { color: '#e53935' },
  },
};

interface StripeFormProps {
  clientSecret: string;
  amount: number;
  onSuccess: (intentId: string) => void;
  onCancel: () => void;
}

const StripeCheckoutForm: React.FC<StripeFormProps> = ({ clientSecret, amount, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setCardError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) { setIsProcessing(false); return; }

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: cardElement },
    });

    if (error) {
      setCardError(error.message || 'Zahlung fehlgeschlagen');
      setIsProcessing(false);
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setCardError('Zahlung nicht abgeschlossen. Bitte versuchen Sie es erneut.');
      setIsProcessing(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
        Betrag: <strong>{amount.toFixed(2).replace('.', ',')} € (zzgl. 19% MwSt.)</strong>
      </Typography>
      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.5, mb: 2, backgroundColor: '#fafafa' }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>
      {cardError && (
        <Alert severity="error" sx={{ mb: 2 }}>{cardError}</Alert>
      )}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={isProcessing} sx={{ color: '#555' }}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isProcessing || !stripe}
          sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 }}
        >
          {isProcessing ? <CircularProgress size={20} color="inherit" /> : 'Jetzt bezahlen'}
        </Button>
      </Stack>
    </Box>
  );
};

const DataRow: React.FC<{ label: string; value: React.ReactNode; masked?: boolean }> = ({ label, value, masked }) => (
  <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 1 }}>
    <Typography sx={{ width: 240, flexShrink: 0, fontWeight: 600, color: '#333', fontSize: '0.9rem', pr: 2 }}>
      {label}:
    </Typography>
    <Typography sx={{ color: masked ? '#bbb' : '#555', fontSize: '0.9rem', fontStyle: masked ? 'italic' : 'normal' }}>
      {value}
    </Typography>
  </Box>
);

const ContractorInquiryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [inquiry, setInquiry] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState<'method' | 'stripe' | 'paypal'>('method');
  const [checkoutData, setCheckoutData] = useState<any>(null);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [isInitiating, setIsInitiating] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement>(null);

  const loadInquiry = async () => {
    setIsLoading(true);
    try {
      const response = await contractorAPI.getJobDetail(Number(id));
      setInquiry(response.data);
    } catch {
      addToast('Ausschreibung nicht gefunden', 'error');
      navigate('/contractor/browse-jobs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInquiry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleOpenPayment = () => {
    setPaymentStep('method');
    setCheckoutData(null);
    setStripePromise(null);
    setConfirmOpen(true);
  };

  const handleSelectStripe = async () => {
    setIsInitiating(true);
    try {
      const res = await contractorAPI.createCheckout(Number(id), 'stripe');
      setCheckoutData(res.data);
      setStripePromise(loadStripe(res.data.publishable_key));
      setPaymentStep('stripe');
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Fehler beim Initialisieren der Zahlung', 'error');
      setConfirmOpen(false);
    } finally {
      setIsInitiating(false);
    }
  };

  const handleStripeSuccess = async (paymentIntentId: string) => {
    setIsPurchasing(true);
    try {
      await contractorAPI.purchaseLead(Number(id), { stripe_payment_intent_id: paymentIntentId });
      setConfirmOpen(false);
      addToast('Lead erfolgreich erworben! Kontaktdaten sind jetzt sichtbar.', 'success');
      await loadInquiry();
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Fehler beim Abschließen des Kaufs', 'error');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleSelectPayPal = async () => {
    setIsInitiating(true);
    try {
      const res = await contractorAPI.createCheckout(Number(id), 'paypal');
      const data = res.data;
      setCheckoutData(data);
      setPaymentStep('paypal');

      await new Promise<void>((resolve) => {
        const existingScript = document.getElementById('paypal-sdk');
        if (existingScript) { existingScript.remove(); }
        const script = document.createElement('script');
        script.id = 'paypal-sdk';
        script.src = `https://www.paypal.com/sdk/js?client-id=${data.client_id}&currency=EUR`;
        script.onload = () => resolve();
        document.head.appendChild(script);
      });

      setTimeout(() => {
        const container = paypalContainerRef.current;
        if (!container || !(window as any).paypal) return;
        container.innerHTML = '';
        (window as any).paypal.Buttons({
          createOrder: () => data.order_id,
          onApprove: async (_data: any) => {
            setIsPurchasing(true);
            try {
              await contractorAPI.purchaseLead(Number(id), { paypal_order_id: data.order_id });
              setConfirmOpen(false);
              addToast('Lead erfolgreich erworben! Kontaktdaten sind jetzt sichtbar.', 'success');
              await loadInquiry();
            } catch (err: any) {
              addToast(err?.response?.data?.message || 'Fehler beim Abschließen des Kaufs', 'error');
            } finally {
              setIsPurchasing(false);
            }
          },
          onError: () => {
            addToast('PayPal-Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.', 'error');
          },
          onCancel: () => {
            setPaymentStep('method');
          },
        }).render(container);
      }, 100);
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Fehler beim Initialisieren von PayPal', 'error');
      setConfirmOpen(false);
    } finally {
      setIsInitiating(false);
    }
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  if (!inquiry) return null;

  const purchased = inquiry.already_purchased;
  const soldOut = inquiry.sold_out && !purchased;
  const leadPrice = parseFloat(inquiry.lead_price) || 29.99;
  const purchaseLimit = inquiry.purchase_limit ?? 4;
  const purchasesCount = inquiry.purchases_count ?? 0;

  const getDirectionLabel = (dir: string) => {
    if (dir === 'inbound') return 'entgegenzunehmen (Inbound)';
    if (dir === 'outbound') return 'aktiv zu tätigen (Outbound/Rebound)';
    if (dir === 'both') return 'entgegenzunehmen (Inbound) & aktiv zu tätigen (Outbound/Rebound)';
    return dir || '-';
  };

  return (
    <PortalLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/contractor/browse-jobs')}
          sx={{ color: '#E85D26', fontWeight: 600, mb: 2 }}
        >
          zurück
        </Button>

        {purchased && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Sie haben die Kontaktdaten dieser Ausschreibung bereits erworben. Alle Informationen sind sichtbar.
          </Alert>
        )}

        {soldOut && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Diese Ausschreibung ist ausverkauft und kann nicht mehr erworben werden.
          </Alert>
        )}
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 4, py: 3, borderBottom: '2px solid #E85D26' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#E85D26' }}>
            Nummer #{inquiry.id}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Erstellt am {new Date(inquiry.created_at).toLocaleDateString('de-DE')}
          </Typography>
        </Box>

        <Box sx={{ px: 4, py: 3 }}>
          <DataRow label="Firma" value={inquiry.company_name || 'XXX'} masked={!purchased} />
          <DataRow label="Gesellschaftsform" value={inquiry.society_form || '-'} />
          <DataRow label="Anrede" value={inquiry.salutation || 'XXX'} masked={!purchased} />
          <DataRow label="Vorname" value={inquiry.contact_name ? inquiry.contact_name.split(' ')[0] : 'XXX'} masked={!purchased} />
          <DataRow label="Nachname" value={inquiry.contact_name ? inquiry.contact_name.split(' ').slice(1).join(' ') || 'XXX' : 'XXX'} masked={!purchased} />
          <DataRow label="Strasse/Nr." value={inquiry.strasse || 'XXX'} masked={!purchased} />
          <DataRow label="PLZ" value={inquiry.zip_code || 'XXX'} masked={!purchased} />
          <DataRow label="Ort" value={inquiry.city || 'XXX'} masked={!purchased} />
          <DataRow label="Land" value={inquiry.land || 'Deutschland'} />
          <DataRow label="Telefon" value={inquiry.contact_phone || 'XXX'} masked={!purchased} />
          <DataRow label="Homepage" value={inquiry.homepage || (purchased ? '–' : 'XXX')} masked={!purchased} />
          <DataRow label="E-Mail" value={inquiry.contact_email || 'XXX'} masked={!purchased} />
          {inquiry.description && (
            <DataRow label="Bemerkung / Projektbeschreibung" value={inquiry.description} />
          )}
          <DataRow label="Sprachaufzeichnung" value={inquiry.recording_required ? 'Ja' : 'Nein'} />
        </Box>

        {inquiry.portal_note && (
          <Box sx={{ px: 4, py: 3, backgroundColor: '#f0f4ff', borderTop: '1px solid #dce3ff' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
              Anmerkung des Portals
            </Typography>
            <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
              {inquiry.portal_note}
            </Typography>
          </Box>
        )}

        {!purchased && !soldOut && (
          <Box sx={{ px: 4, py: 3, backgroundColor: '#fffaf7', borderTop: '1px solid #f0e0d0' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    Ja, ich möchte die Kontaktdaten für{' '}
                    <strong>{leadPrice.toFixed(2).replace('.', ',')} € (zzgl. 19% MwSt.)</strong> erwerben.
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem' }}>
                  Diese Ausschreibung wird an max. {purchaseLimit} Unternehmen weitergeleitet. Bisher {purchasesCount} mal vermittelt.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<LockIcon />}
                onClick={handleOpenPayment}
                disabled={isPurchasing}
                sx={{
                  background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)',
                  fontWeight: 700,
                  px: 4,
                  '&:hover': { background: 'linear-gradient(135deg, #d94f1e 0%, #b8431a 100%)' },
                }}
              >
                {isPurchasing ? <CircularProgress size={20} color="inherit" /> : 'Verbindlich bestellen'}
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ px: 4, py: 3, borderTop: '1px solid #f0f0f0' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e' }}>
            Ausschreibungsdaten
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
            Diese Kontaktdaten dürfen an <strong>max. {purchaseLimit} Unternehmen</strong> weitergeleitet werden.{' '}
            Diese Daten wurden bisher <strong>{purchasesCount} mal vermittelt</strong>.
          </Typography>

          {inquiry.description && (
            <Typography variant="body2" sx={{ color: '#444', mb: 3, lineHeight: 1.7 }}>
              {inquiry.description}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {inquiry.nearshoring && (
            <DataRow label="Ist Nearshoring eine denkbare Option?" value={inquiry.nearshoring} />
          )}
          {inquiry.order_probability && (
            <DataRow label="Auftragswahrscheinlichkeit" value={inquiry.order_probability} />
          )}
          {inquiry.additional_services && (
            <DataRow label="Zusatzleistungen" value={inquiry.additional_services} />
          )}
          {inquiry.quality_requirements && (
            <DataRow label="Qualitätssicherung" value={inquiry.quality_requirements} />
          )}
          {inquiry.employee_count && (
            <DataRow label="Anzahl Mitarbeiter" value={inquiry.employee_count} />
          )}
          {inquiry.project_remuneration && (
            <DataRow label="Projektvergütung" value={inquiry.project_remuneration} />
          )}
          <DataRow
            label="Callcenter wird beauftragt, um Anrufe"
            value={getDirectionLabel(inquiry.direction)}
          />
          {inquiry.inbound_type && inquiry.direction !== 'outbound' && (
            <DataRow label="Art des Inbounds" value={inquiry.inbound_type} />
          )}
          {inquiry.outbound_type && inquiry.direction !== 'inbound' && (
            <DataRow label="Art des Outbounds" value={inquiry.outbound_type} />
          )}
          {inquiry.outbound_address_source && inquiry.direction !== 'inbound' && (
            <DataRow label="Wer liefert die Adressen für den Outbound?" value={inquiry.outbound_address_source === 'customer' ? 'werden vom Auftraggeber gestellt' : 'sollen vom Callcenter gestellt werden'} />
          )}
          {inquiry.service_hours && (
            <DataRow label="Servicezeiten" value={inquiry.service_hours} />
          )}
          {inquiry.languages && (
            <DataRow label="Sprachen" value={inquiry.languages} />
          )}
          {inquiry.customer_segment && (
            <DataRow label="Kundensegment" value={inquiry.customer_segment} />
          )}
          {inquiry.project_start && (
            <DataRow label="Projektstart" value={inquiry.project_start} />
          )}
          {inquiry.project_end && (
            <DataRow label="Projektende" value={inquiry.project_end} />
          )}
          {inquiry.industry && (
            <DataRow label="Branche des ausschreibenden Unternehmens" value={inquiry.industry} />
          )}
          {inquiry.avg_call_duration && (
            <DataRow label="durchschnittliche Gesprächslänge" value={inquiry.avg_call_duration} />
          )}
          {inquiry.call_volume && (
            <DataRow label="Callvolumen" value={`${inquiry.call_volume}${inquiry.calls_per_unit ? ` pro ${inquiry.calls_per_unit}` : ''}`} />
          )}
          {inquiry.address_volume && (
            <DataRow label="Menge zu bearbeitender Adressen" value={inquiry.address_volume} />
          )}
          {inquiry.addresses_per && (
            <DataRow label="Adressen pro" value={inquiry.addresses_per} />
          )}
        </Box>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => !isPurchasing && !isInitiating && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {paymentStep === 'method' ? 'Zahlungsmethode wählen' : paymentStep === 'stripe' ? 'Kreditkarte' : 'PayPal'}
        </DialogTitle>
        <DialogContent>
          {paymentStep === 'method' && (
            <Box>
              <DialogContentText sx={{ mb: 3 }}>
                Kontaktdaten für{' '}
                <strong>{leadPrice.toFixed(2).replace('.', ',')} € (zzgl. 19% MwSt.)</strong> erwerben.
                Nach dem Kauf sind alle Daten sofort sichtbar.
              </DialogContentText>
              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={isInitiating ? <CircularProgress size={18} /> : <CreditCardIcon />}
                  onClick={handleSelectStripe}
                  disabled={isInitiating}
                  sx={{ justifyContent: 'flex-start', py: 1.5, borderColor: '#E85D26', color: '#E85D26', fontWeight: 600, '&:hover': { borderColor: '#c94d1e', backgroundColor: '#fff5f2' } }}
                >
                  Kreditkarte (Stripe)
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={isInitiating ? <CircularProgress size={18} /> : undefined}
                  onClick={handleSelectPayPal}
                  disabled={isInitiating}
                  sx={{ justifyContent: 'flex-start', py: 1.5, borderColor: '#0070ba', color: '#0070ba', fontWeight: 600, '&:hover': { borderColor: '#005ea6', backgroundColor: '#f0f7ff' } }}
                >
                  PayPal
                </Button>
              </Stack>
            </Box>
          )}

          {paymentStep === 'stripe' && stripePromise && checkoutData && (
            <Elements stripe={stripePromise}>
              <StripeCheckoutForm
                clientSecret={checkoutData.client_secret}
                amount={checkoutData.amount}
                onSuccess={handleStripeSuccess}
                onCancel={() => setConfirmOpen(false)}
              />
            </Elements>
          )}

          {paymentStep === 'paypal' && (
            <Box>
              {isPurchasing ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                    Betrag: <strong>{leadPrice.toFixed(2).replace('.', ',')} € (zzgl. 19% MwSt.)</strong>
                  </Typography>
                  <Box ref={paypalContainerRef} sx={{ minHeight: 50 }} />
                  <Button onClick={() => setPaymentStep('method')} sx={{ mt: 1, color: '#555' }}>
                    ← Andere Zahlungsmethode wählen
                  </Button>
                </>
              )}
            </Box>
          )}

          {paymentStep === 'stripe' && isPurchasing && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        {paymentStep === 'method' && (
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={isInitiating} sx={{ color: '#555' }}>Abbrechen</Button>
          </DialogActions>
        )}
      </Dialog>
    </PortalLayout>
  );
};

export default ContractorInquiryDetailPage;
